import {computed, inject} from '@angular/core';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {
  DEFAULT_CATEGORY_STATE,
  MachineListCategoryPreferences,
  MachineListCategoryState,
  MachineListSortDirection,
  MachineListViewUserConfig,
} from '@models/dashboard/machine-list/machine-list-view-user-config';
import {MachineListUserConfigService} from '@services/dashboard/machine-list-user-config.service';

interface ExpandedDetailsState {
  expandedMachineId: number | null;
  expandedDetails: unknown | null;
  expandedMetadata: unknown | null;
  isExpandedLoading: boolean;
}

interface MachineListViewState extends ExpandedDetailsState {
  activeCategory: string;
  categories: Record<string, MachineListCategoryState>;
}

function defaultState(): MachineListViewState {
  return {
    activeCategory: '',
    categories: {},
    expandedMachineId: null,
    expandedDetails: null,
    expandedMetadata: null,
    isExpandedLoading: false,
  };
}

function categoryStateOrDefault(
  categories: Record<string, MachineListCategoryState>,
  categoryName: string,
): MachineListCategoryState {
  return categories[categoryName] ?? {...DEFAULT_CATEGORY_STATE};
}

function toPersistedCategories(
  categories: Record<string, MachineListCategoryState>,
): Record<string, MachineListCategoryPreferences> {
  return Object.entries(categories).reduce<Record<string, MachineListCategoryPreferences>>(
    (result, [categoryName, category]) => {
      result[categoryName] = {
        itemsPerPage: category.itemsPerPage,
        visibleColumns: [...category.visibleColumns],
      };
      return result;
    },
    {},
  );
}

function toRuntimeCategories(
  categories: Record<string, MachineListCategoryPreferences>,
): Record<string, MachineListCategoryState> {
  return Object.entries(categories ?? {}).reduce<Record<string, MachineListCategoryState>>(
    (result, [categoryName, category]) => {
      result[categoryName] = {
        ...DEFAULT_CATEGORY_STATE,
        itemsPerPage: category.itemsPerPage,
        visibleColumns: [...category.visibleColumns],
      };
      return result;
    },
    {},
  );
}

export const MachineListViewStore = signalStore(
  withState<MachineListViewState>(defaultState()),
  withComputed((store) => ({
    activeCategoryState: computed<MachineListCategoryState>(() => {
      const name = store.activeCategory();
      if (!name) {
        return {...DEFAULT_CATEGORY_STATE};
      }
      return categoryStateOrDefault(store.categories(), name);
    }),
  })),
  withMethods((store) => {
    const configService = inject(MachineListUserConfigService);

    function persist(): void {
      const snapshot: MachineListViewUserConfig = {
        activeCategory: store.activeCategory(),
        categories: toPersistedCategories(store.categories()),
      };
      configService.save(snapshot);
    }

    return {
      hydrateFromConfig(config: MachineListViewUserConfig): void {
        patchState(store, () => ({
          activeCategory: config.activeCategory ?? '',
          categories: toRuntimeCategories(config.categories ?? {}),
          expandedMachineId: null,
          expandedDetails: null,
          expandedMetadata: null,
          isExpandedLoading: false,
        }));
      },

      setActiveCategory(name: string): void {
        patchState(store, () => ({
          activeCategory: name,
          expandedMachineId: null,
          expandedDetails: null,
          expandedMetadata: null,
          isExpandedLoading: false,
        }));
        persist();
      },

      setSort(column: string, direction: MachineListSortDirection): void {
        patchState(store, (state) => {
          const name = state.activeCategory;
          if (!name) {
            return {};
          }
          const current = categoryStateOrDefault(state.categories, name);
          const merged: MachineListCategoryState = {...current, sortColumn: column, sortDirection: direction, currentPage: 1};
          return {categories: {...state.categories, [name]: merged}};
        });
        persist();
      },

      setSearchText(text: string): void {
        patchState(store, (state) => {
          const name = state.activeCategory;
          if (!name) {
            return {};
          }
          const current = categoryStateOrDefault(state.categories, name);
          const merged: MachineListCategoryState = {...current, searchText: text, currentPage: 1};
          return {categories: {...state.categories, [name]: merged}};
        });
        persist();
      },

      setPage(page: number): void {
        patchState(store, (state) => {
          const name = state.activeCategory;
          if (!name) {
            return {};
          }
          const current = categoryStateOrDefault(state.categories, name);
          const merged: MachineListCategoryState = {...current, currentPage: page};
          return {categories: {...state.categories, [name]: merged}};
        });
        persist();
      },

      setItemsPerPage(itemsPerPage: number): void {
        patchState(store, (state) => {
          const name = state.activeCategory;
          if (!name) {
            return {};
          }
          const current = categoryStateOrDefault(state.categories, name);
          const merged: MachineListCategoryState = {...current, itemsPerPage, currentPage: 1};
          return {categories: {...state.categories, [name]: merged}};
        });
        persist();
      },

      setVisibleColumns(columns: string[]): void {
        patchState(store, (state) => {
          const name = state.activeCategory;
          if (!name) {
            return {};
          }
          const current = categoryStateOrDefault(state.categories, name);
          const merged: MachineListCategoryState = {...current, visibleColumns: columns};
          return {categories: {...state.categories, [name]: merged}};
        });
        persist();
      },

      ensureCategoryInitialized(categoryName: string, allColumnNames: string[]): void {
        const categories = store.categories();
        if (categories[categoryName]) {
          return;
        }
        const visibleColumns = allColumnNames && allColumnNames.length > 0 ? [...allColumnNames] : [];
        const newState: MachineListCategoryState = {...DEFAULT_CATEGORY_STATE, visibleColumns};
        patchState(store, () => ({categories: {...categories, [categoryName]: newState}}));
        persist();
      },

      setExpandedMachine(machineId: number | null): void {
        const currentId = store.expandedMachineId();
        if (currentId === machineId) {
          patchState(store, () => ({expandedMachineId: null, expandedDetails: null, expandedMetadata: null, isExpandedLoading: false}));
          return;
        }
        patchState(store, () => ({expandedMachineId: machineId, expandedDetails: null, expandedMetadata: null, isExpandedLoading: true}));
      },

      setExpandedDetails(details: unknown): void {
        patchState(store, () => ({expandedDetails: details, isExpandedLoading: false}));
      },

      setExpandedMetadata(metadata: unknown): void {
        patchState(store, () => ({expandedMetadata: metadata}));
      },

      setExpandedLoading(loading: boolean): void {
        patchState(store, () => ({isExpandedLoading: loading}));
      },

      resetExpanded(): void {
        patchState(store, () => ({expandedMachineId: null, expandedDetails: null, expandedMetadata: null, isExpandedLoading: false}));
      },

      snapshot(): MachineListViewUserConfig {
        return {
          activeCategory: store.activeCategory(),
          categories: toPersistedCategories(store.categories()),
        };
      },
    };
  }),
);

export type MachineListViewStoreInstance = InstanceType<typeof MachineListViewStore>;
